import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { EmptyList } from '@components/EmptyList';
import { Anchor } from '@components/ui/anchor';
import { Pagination } from '@components/ui/pagination';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatFullName } from '@formatters/fullName.formatter';
import {
  getAllServiceRequestsSchema,
  type GetAllServiceRequestsParams,
} from '@services/serviceRequests/dtos/getAll.dto';
import { serviceRequestService } from '@services/serviceRequests/service';

import { ServiceRequestFilters } from './@components/Filters';
import { ServiceRequestTableCell } from './@components/TableCell';

function serviceRequestsQueryOptions(params: GetAllServiceRequestsParams) {
  return queryOptions({
    staleTime: EStaleTime.OneHour,
    queryKey: queryKeys.serviceRequests.list(params),
    queryFn: () => serviceRequestService.getAll(params),
  });
}

export const Route = createFileRoute('/_layout/(private)/service-requests/')({
  component: ServiceRequests,
  validateSearch: search => getAllServiceRequestsSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const options = serviceRequestsQueryOptions(deps);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingMs: 300,
  pendingMinMs: 600,
});

function ServiceRequests() {
  const searchParams = Route.useSearch();

  const { data } = useSuspenseQuery(serviceRequestsQueryOptions(searchParams));

  return (
    <div className="relative">
      <ServiceRequestFilters />
      <div
        className="bg-background dark:border-primary-light-one mb-2 grid w-full overflow-hidden rounded-xl border border-zinc-200 shadow"
        role="table"
      >
        <div
          className="bg-primary-light-two dark:text-primary-dark-one grid h-12 grid-cols-8 items-center font-medium text-zinc-600"
          role="row"
        >
          {headers.map(header => (
            <span
              className="text-center text-sm font-medium"
              key={header.accessorKey}
              role="columnheader"
            >
              {header.title}
            </span>
          ))}
        </div>

        {data.list.map(row => (
          <Link
            className="bg-row-odd even:bg-row-even hover:bg-row-odd-hover even:hover:bg-row-even-hover grid min-h-14 grid-cols-8 items-center justify-between transition-all duration-200"
            key={row.id}
            params={{ id: row.id }}
            role="row"
            to="/service-requests/$id"
          >
            <ServiceRequestTableCell type="id" value={row.displayId} />
            <ServiceRequestTableCell type="date" value={row.createdAt} />
            <ServiceRequestTableCell
              value={formatFullName({
                firstName: row.requester.firstName,
                lastName: row.requester.lastName,
              })}
            />
            <ServiceRequestTableCell
              value={row.requester.language.toUpperCase()}
            />
            <ServiceRequestTableCell value={row.postcode} />
            <ServiceRequestTableCell
              color={row.businessLine.color}
              iconName={row.businessLine.icon}
              value={row.businessLine.translations.en}
            />
            <ServiceRequestTableCell value={row.interventionCount} />
            <ServiceRequestTableCell type="tag" value={row.status} />
          </Link>
        ))}

        <EmptyList
          alt="No service requests animation/image"
          description="Click the + button to create a service request"
          isVisible={!data.list.length}
          title="No service requests yet."
        />
      </div>
      <div className="ml-auto flex w-fit items-center">
        <span className="mr-4 text-sm text-gray-500 dark:text-gray-300">
          Total: {data.count || 0} service request(s)
        </span>
        <Pagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
        />
      </div>

      <Anchor
        className="size-12"
        float
        rounded="full"
        size="icon"
        to="/service-requests/create"
        variant="primary"
      >
        <Plus size={24} />
      </Anchor>
    </div>
  );
}

const headers = [
  { title: 'ID', accessorKey: 'displayId' },
  { title: 'Creation date', accessorKey: 'createdAt' },
  { title: 'Requester', accessorKey: 'requesterFullName' },
  { title: 'Language', accessorKey: 'language' },
  { title: 'Postcode', accessorKey: 'postcode' },
  { title: 'Business line', accessorKey: 'businessLine.translations.en' },
  { title: 'Interventions', accessorKey: 'interventionCount' },
  { title: 'Status', accessorKey: 'status' },
];
